import {
  isProjectAngleValidating,
  type ProjectAngle,
} from '@/types/project-angle';

/**
 * A library view whose wait budget ran out. Keyed by angle id; `updatedAt` is
 * the server row version it was given up on, so the same row coming back from
 * a refetch stays given up (no new poll), and a changed row is the server
 * talking again.
 */
export interface StalledView {
  updatedAt: string;
  message: string;
}

export interface LibraryWatchPlan {
  /** Validating rows that still have budget: the watch polls for these. */
  watchIds: string[];
  /** Validating rows already given up on at this same version: shown as the error again, never polled. */
  restall: Map<string, string>;
  /** Stalled entries the server has since moved on from (or removed). */
  released: string[];
}

/**
 * Splits the library list into what to poll and what to leave alone. Pure, so
 * the stop rule (once given up, a row is polled again only after the server
 * changes it) is testable without timers.
 */
export function planLibraryWatch(
  angles: readonly ProjectAngle[],
  stalled: ReadonlyMap<string, StalledView>
): LibraryWatchPlan {
  const byId = new Map(angles.map((angle) => [angle.id, angle]));
  const watchIds: string[] = [];
  const restall = new Map<string, string>();
  const released: string[] = [];

  for (const [id, entry] of stalled) {
    const angle = byId.get(id);
    if (!angle || angle.attributes.updated_at !== entry.updatedAt) {
      released.push(id);
    }
  }

  for (const angle of angles) {
    if (!isProjectAngleValidating(angle)) continue;
    const entry = stalled.get(angle.id);
    if (entry && entry.updatedAt === angle.attributes.updated_at) {
      restall.set(angle.id, entry.message);
    } else {
      watchIds.push(angle.id);
    }
  }

  watchIds.sort();
  return { watchIds, restall, released };
}

/**
 * Budget already spent when the watched set changes. A set that only lost
 * rows (a verdict landed) keeps counting; any new row starts a fresh wait.
 */
export function carriedWaitMs(
  previousIds: readonly string[],
  previousWaitedMs: number,
  nextIds: readonly string[]
): number {
  const previous = new Set(previousIds);
  return nextIds.every((id) => previous.has(id)) ? previousWaitedMs : 0;
}

/**
 * The given-up rows as a provisional technical error carrying `message`. Lives
 * only in the query cache: the next server version of the row replaces it.
 */
export function withStalledViews(
  angles: readonly ProjectAngle[],
  messages: ReadonlyMap<string, string>
): ProjectAngle[] {
  return angles.map((angle) => {
    const message = messages.get(angle.id);
    if (message === undefined || !isProjectAngleValidating(angle)) {
      return angle;
    }
    return {
      ...angle,
      attributes: {
        ...angle.attributes,
        validation: {
          ...angle.attributes.validation,
          status: 'technical_error',
          is_technical_error: true,
          recommendations: message,
        },
      },
    };
  });
}
